import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  DocumentIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  PhotoIcon,
  TableCellsIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import {
  useLanguageContext,
  useNotificationContext,
  useUserContext
} from '../../context'
import { query } from '../../graphql'
import {
  listDocuments,
  parseAdditionalData,
  replacementFileName
} from '../../helpers/talent-details'
import { useSpaceFiles } from '../../hooks/use-space-files'
import ActivityIndicator from '../activity-indicator'
import Badge from '../tailwind/Badge'
import Button from '../tailwind/Button'
import Panel from '../tailwind/Panel'
import SectionHeading from '../tailwind/SectionHeading'

export const getFileTypeVisual = (extension) => {
  const ext = (extension || '').toLowerCase()
  if (ext === 'pdf')
    return {
      Icon: DocumentTextIcon,
      band: 'bg-gradient-to-br from-emerald-400 to-teal-500'
    }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic'].includes(ext))
    return {
      Icon: PhotoIcon,
      band: 'bg-gradient-to-br from-sky-400 to-blue-500'
    }
  if (['xls', 'xlsx', 'csv', 'numbers'].includes(ext))
    return {
      Icon: TableCellsIcon,
      band: 'bg-gradient-to-br from-teal-400 to-cyan-500'
    }
  if (['doc', 'docx', 'txt', 'rtf', 'pages'].includes(ext))
    return {
      Icon: DocumentTextIcon,
      band: 'bg-gradient-to-br from-indigo-400 to-violet-500'
    }
  return {
    Icon: DocumentIcon,
    band: 'bg-gradient-to-br from-slate-400 to-slate-500'
  }
}

// Wie im Bewerbungsformular und in der App. Die Grenze erzwingt S3 ueber die
// Upload-Policy, hier geht es nur um Feedback vor dem Hochladen.
const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx']
const MAX_FILE_SIZE = 20 * 1024 * 1024

const parseFileControl = (response) => {
  const { statusCode, body } = JSON.parse(response) || {}
  return { statusCode, body: body ? JSON.parse(body) : {} }
}

// presigned POST an S3: alle Policy-Felder zuerst, die Datei als letztes Feld.
const uploadToS3 = async ({ spaceId, fileName, file }) => {
  const { statusCode, body } = parseFileControl(
    await query({
      query: 'fileControl',
      variables: {
        space_id: spaceId,
        action: 'getUploadUrl',
        file_name: fileName
      }
    })
  )
  if (statusCode !== 200 || !body.url) {
    throw new Error(
      `getUploadUrl fehlgeschlagen (${statusCode}): ${body.error}`
    )
  }

  const form = new FormData()
  Object.entries(body.fields || {}).forEach(([key, value]) =>
    form.append(key, value)
  )
  form.append('file', file, file.name)

  const res = await fetch(body.url, { method: 'POST', body: form })
  if (res.status !== 204) {
    const text = await res.text().catch(() => '')
    const code = (text.match(/<Code>([^<]+)<\/Code>/) || [])[1]
    throw new Error(`S3-Upload fehlgeschlagen (${res.status} ${code || ''})`)
  }

  // Der Server bereinigt den Namen ggf. noch (/ und \), massgeblich ist seiner
  return body.file_name || fileName
}

const deleteFile = async ({ spaceId, fileName, replacedBy }) => {
  const { statusCode, body } = parseFileControl(
    await query({
      query: 'fileControlDeleteFile',
      variables: {
        space_id: spaceId,
        file_name: fileName,
        replaced_by: replacedBy
      }
    })
  )
  if (statusCode !== 200) {
    throw new Error(`deleteFile fehlgeschlagen (${statusCode}): ${body.error}`)
  }
}

const DocumentCard = ({
  doc,
  downloadingKey,
  busyKey,
  canEdit,
  onDownload,
  onReplace,
  onDelete
}) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()

  const { file, meta } = doc
  const { Icon, band } = getFileTypeVisual(meta.extension)
  const isBusy = busyKey === file.key

  return (
    <li className='overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200'>
      <div className={`flex h-14 items-center gap-2 px-4 ${band}`}>
        <Icon className='size-6 flex-shrink-0 text-white' />
        {meta.extension && (
          <span className='text-xs font-semibold uppercase tracking-wide text-white/90'>
            {meta.extension}
          </span>
        )}
      </div>

      <div className='p-4'>
        <p
          className='truncate text-sm font-semibold text-gray-900'
          title={meta.displayName}
        >
          {meta.displayName}
        </p>
        <div className='mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500'>
          {meta.uploadedAt && (
            <span>
              {t('pages.talent.documents_uploaded_at', {
                date: format(meta.uploadedAt, 'd. MMMM yyyy', {
                  locale: { de, en }[language]
                })
              })}
            </span>
          )}
          {meta.uploadedAt && meta.questionLabel && (
            <span className='text-gray-300'>·</span>
          )}
          {meta.questionLabel && (
            <span className='truncate'>
              {t('pages.talent.documents_answer_to', {
                label: meta.questionLabel
              })}
            </span>
          )}
        </div>
        <div className='mt-3 flex flex-wrap items-center gap-2'>
          <Button.SoftSM
            text={t('pages.talent.space_details_download')}
            icon={<ArrowDownTrayIcon className='size-4' />}
            isLoading={downloadingKey === file.key}
            onClick={() => onDownload(file.key)}
            minWidth='min-w-0'
          />
          {canEdit && (
            <>
              <Button.SecondarySM
                text={t('pages.talent.document_replace')}
                icon={<ArrowPathIcon className='size-4' />}
                isLoading={isBusy}
                disabled={!!busyKey}
                onClick={() => onReplace(doc)}
                minWidth='min-w-0'
              />
              <Button.SecondarySM
                text={t('pages.talent.document_delete')}
                icon={<TrashIcon className='size-4' />}
                disabled={!!busyKey}
                onClick={() => onDelete(doc)}
                minWidth='min-w-0'
              />
            </>
          )}
        </div>
      </div>
    </li>
  )
}

DocumentCard.propTypes = {
  doc: PropTypes.object.isRequired,
  downloadingKey: PropTypes.string,
  busyKey: PropTypes.string,
  canEdit: PropTypes.bool.isRequired,
  onDownload: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

const SpaceDocuments = ({ space, refetchSpace }) => {
  const { t } = useTranslation()
  const { alert, error, success } = useNotificationContext()
  const { isAdmin, activeSpace } = useUserContext()
  const queryClient = useQueryClient()

  const [downloadingKey, setDownloadingKey] = useState(null)
  // key der Datei, die gerade ersetzt oder geloescht wird
  const [busyKey, setBusyKey] = useState(null)
  const fileInputRef = useRef(null)
  const replaceTargetRef = useRef(null)

  // Loeschen/Ersetzen wie das Loeschen von Talenten: Admin und Manager (Rolle
  // __delete), das Backend prueft dasselbe.
  const canEdit = !!(isAdmin || activeSpace?.authorization_role?.crud?.delete)

  const { fileFields } = parseAdditionalData(space)
  // Dieselbe Liste liest der PDF-Dialog. Ein Fehler zeigt wie bisher den
  // leeren Zustand, die Ursache landet ueber console.error in Bugsnag.
  const {
    data: files = [],
    isPending: loading,
    error: filesError
  } = useSpaceFiles(space.id)

  useEffect(() => {
    if (filesError) console.error(filesError)
  }, [filesError])

  // Die Datei kommt nicht mehr als base64 durch AppSync (Antworten sind dort
  // auf 5 MB gedeckelt, ab ~3,7 MB brach der Download ab), sondern direkt von
  // S3 ueber eine signierte URL. Die URL setzt Content-Disposition mit dem
  // echten Dateinamen, Navigation reicht fuer den Download. Sie ist 10 Minuten
  // gueltig, deshalb bei jedem Klick eine neue holen.
  const handleDownloadFile = (fileKey) => {
    const fileName = fileKey.split('/').pop()
    setDownloadingKey(fileKey)

    query({
      query: 'fileControl',
      variables: {
        space_id: space.id,
        action: 'getDownloadUrl',
        file_name: fileName
      }
    })
      .then((response) => {
        const { statusCode, body } = JSON.parse(response) || {}
        const bodyData = body ? JSON.parse(body) : {}

        if (statusCode === 404) {
          error(t('pages.talent.document_not_found'))
          console.error('Dokument nicht gefunden:', fileName)
          return
        }

        if (statusCode !== 200 || !bodyData?.url) {
          throw new Error(
            `getDownloadUrl fehlgeschlagen (${statusCode}): ${bodyData?.error}`
          )
        }

        window.location.assign(bodyData.url)
      })
      .catch((err) => {
        error()
        console.error('Fehler beim Abrufen der Datei:', err)
      })
      .finally(() => setDownloadingKey(null))
  }

  // Dateiliste, Zaehler im Space und Verlauf (neuer Log-Eintrag) neu laden
  const refreshAfterChange = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.spaceFiles(space.id) })
    if (refetchSpace) refetchSpace()
  }

  const handleDelete = ({ file, meta }) => {
    alert(
      t('pages.talent.document_delete_confirm_title'),
      [
        t('pages.talent.document_delete_confirm_text', {
          name: meta.displayName
        })
      ],
      [
        {
          text: t('pages.talent.document_delete'),
          style: 'destructive',
          onPress: () => {
            setBusyKey(file.key)
            deleteFile({ spaceId: space.id, fileName: meta.filename })
              .then(() => {
                success(t('pages.talent.document_deleted'))
                refreshAfterChange()
              })
              .catch((err) => {
                error()
                console.error('Fehler beim Loeschen des Dokuments:', err)
              })
              .finally(() => setBusyKey(null))
          }
        },
        { text: t('pages.talent.document_cancel'), style: 'cancel' }
      ]
    )
  }

  // Ersetzen: Datei waehlen -> neue Datei hochladen -> alte mit replaced_by
  // loeschen. Erst wenn der Upload durch ist, verschwindet die alte Datei.
  const handleReplace = (doc) => {
    replaceTargetRef.current = doc
    fileInputRef.current?.click()
  }

  const handleReplaceFileChosen = (event) => {
    const newFile = event.target.files?.[0]
    // gleiche Datei nochmal waehlen koennen
    event.target.value = ''
    const target = replaceTargetRef.current
    replaceTargetRef.current = null
    if (!newFile || !target) return

    const extension = (newFile.name.split('.').pop() || '').toLowerCase()
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      error(t('pages.talent.document_replace_invalid_type'))
      return
    }
    if (newFile.size > MAX_FILE_SIZE) {
      error(t('pages.talent.document_replace_too_large'))
      return
    }

    const fileName = replacementFileName(
      target.file.key,
      newFile.name.replace(/\s+/g, '_').replace(/[/\\]/g, '_')
    )

    setBusyKey(target.file.key)
    uploadToS3({ spaceId: space.id, fileName, file: newFile })
      .then((uploadedName) =>
        deleteFile({
          spaceId: space.id,
          fileName: target.meta.filename,
          replacedBy: uploadedName
        })
      )
      .then(() => {
        success(t('pages.talent.document_replaced'))
        refreshAfterChange()
      })
      .catch((err) => {
        error()
        console.error('Fehler beim Ersetzen des Dokuments:', err)
        // Upload kann durch sein, obwohl das Loeschen scheiterte
        refreshAfterChange()
      })
      .finally(() => setBusyKey(null))
  }

  // Sektion nur zeigen, wenn Dokumente vorhanden sind oder welche angefragt wurden
  if (!loading && files.length === 0 && fileFields.length === 0) return null
  if (loading && fileFields.length === 0 && files.length === 0) return null

  return (
    <Panel>
      <section aria-labelledby='documents-title'>
        <SectionHeading
          id='documents-title'
          title={t('pages.talent.space_details_documents_label')}
          description={t('pages.talent.documents_description')}
          actions={
            files.length > 0 && (
              <Badge text={String(files.length)} color='gray' />
            )
          }
        />

        <div className='border-t border-gray-300 pt-5'>
          {loading ? (
            <ActivityIndicator className='flex justify-center py-6' />
          ) : files.length === 0 ? (
            <div className='rounded-lg border border-dashed border-gray-300 bg-white/60 px-6 py-8 text-center'>
              <FolderOpenIcon className='mx-auto size-8 text-gray-300' />
              <p className='mt-2 text-sm text-gray-500'>
                {t('pages.talent.documents_empty')}
              </p>
            </div>
          ) : (
            <ul role='list' className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              {listDocuments(files, fileFields).map((doc) => (
                <DocumentCard
                  key={doc.key}
                  doc={doc}
                  downloadingKey={downloadingKey}
                  busyKey={busyKey}
                  canEdit={canEdit}
                  onDownload={handleDownloadFile}
                  onReplace={handleReplace}
                  onDelete={handleDelete}
                />
              ))}
            </ul>
          )}
        </div>
        {canEdit && (
          <input
            ref={fileInputRef}
            type='file'
            accept='.pdf,.doc,.docx'
            className='hidden'
            onChange={handleReplaceFileChosen}
          />
        )}
      </section>
    </Panel>
  )
}

SpaceDocuments.propTypes = {
  space: PropTypes.object.isRequired,
  refetchSpace: PropTypes.func
}

export default SpaceDocuments
